import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Zones d\'intervention', item: 'https://fenetre-paris15.fr/zones-intervention-paris-15/' },
  { name: 'Javel' },
]);

export default function QuartierJavelPage() {
  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre Quartier Javel (Paris 15) | DF PRO"
        description="Réparation de fenêtres et volets dans le quartier Javel (Paris 15ᵉ). Intervention 24/48h, devis gratuit. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/quartier-javel/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><Link to="/zones-intervention-paris-15/" className="hover:text-brand-accent">Zones</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Javel</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark mb-6">Réparation de fenêtres dans le quartier Javel — Paris 15ᵉ</h1>

          {/* Image Javel */}
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8">
            <img
              src="/images/dfpro-javel.webp"
              alt="Quartier Javel — immeubles modernes bord de Seine et Parc André-Citroën, DF PRO Paris 15"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="prose prose-sm max-w-none text-brand-text/70 leading-relaxed space-y-4">
            <p>
              Le quartier Javel, situé à l'ouest du 15ᵉ arrondissement, est l'un des secteurs les plus transformés de la capitale. Ancien site des usines automobiles <strong>Citroën</strong>, il a connu une reconversion spectaculaire à partir des années 1990 avec la création du Parc André-Citroën et la construction de nombreux immeubles de bureaux et résidences contemporaines.
            </p>
            <p>
              Aujourd'hui, Javel est un quartier dynamique qui mêle habitat récent, sièges d'entreprises, équipements culturels (La Seine Musicale n'est pas loin, sur l'île Seguin) et le vaste poumon vert du Parc André-Citroën. La desserte est excellente : RER C (gare de Javel), métro ligne 10 (station Javel-André Citroën) et de nombreuses lignes de bus.
            </p>

            <h2 className="font-heading text-xl font-bold text-brand-dark mt-8 mb-4">Types de fenêtres et pannes fréquentes à Javel</h2>
            <p>
              Le bâti de Javel est majoritairement <strong>récent et contemporain</strong>. Les résidences construites dans les années 1990-2010 sont équipées de menuiseries <strong>PVC haut de gamme</strong> et d'<strong>aluminium à rupture de pont thermique</strong>. On trouve également des immeubles de bureaux avec de grandes surfaces vitrées et des baies coulissantes. Les pannes typiques que nous rencontrons à Javel concernent :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>La quincaillerie moderne : crémones multipoints, compas, paumelles réglables</li>
              <li>Les joints d'étanchéité qui se dégradent avec l'exposition (le quartier est proche de la Seine)</li>
              <li>Les volets roulants motorisés, très répandus dans les résidences récentes</li>
              <li>Les galets de baies coulissantes sur les grandes ouvertures</li>
              <li>Les mécanismes d'oscillo-battant sur les fenêtres PVC</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-brand-dark mt-8 mb-4">Nos interventions à Javel</h2>
            <p>
              DF PRO connaît parfaitement le quartier Javel et ses spécificités. Nous intervenons régulièrement dans les résidences du boulevard du Général Martial Valin, de la rue Leblanc, des rues autour du Parc André-Citroën et des nouvelles constructions le long de la Seine. Notre proximité géographique nous permet d'intervenir <strong>sous 24 à 48h</strong> avec un diagnostic précis et un devis gratuit.
            </p>

            <div className="bg-brand-mint/30 rounded-xl p-6 mt-8">
              <h2 className="font-heading text-xl font-bold text-brand-dark mb-3">Besoin d'une intervention à Javel ?</h2>
              <p className="text-brand-text/70 text-sm mb-4">Nous nous déplaçons gratuitement pour diagnostiquer votre panne dans le quartier Javel.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-gray-200">
              <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline text-sm">← Toutes les zones d'intervention</Link>
              <Link to="/reparation-fenetre-pvc-paris-15/" className="text-brand-accent hover:underline text-sm">Fenêtres PVC</Link>
              <Link to="/reparation-fenetre-aluminium-paris-15/" className="text-brand-accent hover:underline text-sm">Fenêtres Aluminium</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}